import { LitElement, html } from 'lit';
import { unsafeStatic } from 'lit/static-html.js';
import styles from './studio-select.css.mjs';
import '../studio-icon/studio-icon.mjs';

export class StudioSelect extends LitElement {
  static styles = styles;

  static properties = {
    options: { type: Array, reflect: true },
    selected: { type: Array, reflect: true },
    disabled: { type: Boolean, reflect: true },
    expanded: { type: Boolean, reflect: true },
    singleSelect: { type: Boolean, reflect: true },
    placeholder: { type: String, reflect: true },
    preventNull: { type: Boolean, reflect: true },
    height: { type: String, reflect: true },
    width: { type: String, reflect: true },
    position: { type: String, reflect: true },
  };

  static validHeights = ['tiny', 'small', 'medium', 'large', 'giant'];
  static validPositions = ['absolute', 'fixed', 'sticky'];

  constructor() {
    super();
    this.options = [];
    this.selected = [];
    this.disabled = false;
    this.expanded = false;
    this.singleSelect = false;
    this.preventNull = false;
    this.placeholder = 'Select an option';
    this.height = 'medium';
    this.width = '100%';
    this.position = 'absolute';
    this._eventListeners = [];
  }

  set options(value) {
    this._options = Array.isArray(value)
      ? value.filter(o => o && typeof o === 'object' && 'value' in o && 'label' in o)
      : [];
  }

  get options() {
    return this._options;
  }

  set selected(value) {
    this._selected = Array.isArray(value)
      ? value.filter(v => this.options.some(o => o.value === v))
      : [];
    if (this.singleSelect && this._selected.length > 1) {
      this._selected = this._selected.slice(0, 1);
    }
  }

  get selected() {
    return this._selected;
  }

  set placeholder(value) {
    this._placeholder = typeof value === 'string' ? value : 'Select an option';
  }

  get placeholder() {
    return this._placeholder;
  }

  set height(value) {
    const oldValue = this._height;
    this._height = StudioSelect.validHeights.includes(value) ? value : 'medium';
    this.requestUpdate('height', oldValue);
  }

  get height() {
    return this._height;
  }

  set width(value) {
    this._width = typeof value === 'string' ? value : '100%';
  }

  get width() {
    return this._width;
  }

  set position(value) {
    const oldValue = this._position;
    this._position = StudioSelect.validPositions.includes(value) ? value : 'absolute';
    this.requestUpdate('position', oldValue);
  }

  get position() {
    return this._position;
  }

  firstUpdated() {
    const trigger = this.renderRoot.querySelector('.trigger');
    if (!trigger) return;

    const setState = (state, add = true) => {
      if (!this.disabled && !this.expanded) {
        trigger.classList[add ? 'add' : 'remove'](state);
      }
    };

    this._eventListeners = [
      {
        element: trigger,
        event: 'click',
        handler: () => this._toggleMenu(),
      },
      {
        element: trigger,
        event: 'mouseenter',
        handler: () => setState('hover'),
      },
      {
        element: trigger,
        event: 'mouseleave',
        handler: () => setState('hover', false),
      },
      {
        element: trigger,
        event: 'focusin',
        handler: () => setState('focus'),
      },
      {
        element: trigger,
        event: 'focusout',
        handler: () => setState('focus', false),
      },
      {
        element: trigger,
        event: 'keydown',
        handler: (e) => {
          if (this.disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._toggleMenu();
          } else if (e.key === 'ArrowDown' && !this.expanded) {
            e.preventDefault();
            this.expanded = true;
          } else if (e.key === 'Escape' && this.expanded) {
            e.preventDefault();
            this.expanded = false;
          }
        },
      },
      {
        element: this,
        event: 'focusout',
        handler: (e) => {
          if (this.expanded && !this.contains(e.relatedTarget)) {
            this.expanded = false;
          }
        },
      },
    ];

    this._eventListeners.forEach(({ element, event, handler }) =>
      element.addEventListener(event, handler)
    );
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    const checkSelection = (
      changedProperties.has('options') ||
      changedProperties.has('singleSelect') ||
      changedProperties.has('preventNull')
    );

    const forceSelection = (
      this.options.length > 0 &&
      this.selected.length === 0 &&
      this.preventNull
    );

    if (checkSelection && forceSelection) {
      this.selected = [this.options[0].value];
      this._dispatchChange();
    }

    if (changedProperties.has('width')) {
      this.style.setProperty('--select-width', this.width);
    }

    if (changedProperties.has('expanded')) {
      if (this.expanded) {
        const trigger = this.renderRoot.querySelector('.trigger');
        const triggerRect = trigger?.getBoundingClientRect();
        const menu = this.renderRoot.querySelector('.menu');
        const paddingLeft = trigger ? parseFloat(window.getComputedStyle(trigger).paddingLeft) : 0;

        if (menu && triggerRect) {
          requestAnimationFrame(() => {
            const originalMaxHeight = menu.style.maxHeight || '288px';
            menu.style.maxHeight = 'none';
            const fullHeight = menu.scrollHeight;
            menu.style.maxHeight = originalMaxHeight;

            const maxMenuHeight = 288;

            menu.style.position = this.position;
            menu.style.width = `${triggerRect.width}px`;

            if (this.position === 'fixed') {
              menu.style.left = `${triggerRect.left}px`;
            }
          });
        }

        setTimeout(() => {
          const closeMenu = e => {
            if (!this.contains(e.target)) {
              this.expanded = false;
            }
          };
          document.addEventListener('click', closeMenu);
          this._eventListeners.push({ element: document, event: 'click', handler: closeMenu });
        }, 0);
      } else {
        const docListener = this._eventListeners.find(l => l.element === document && l.event === 'click');
        if (docListener) {
          document.removeEventListener('click', docListener.handler);
          this._eventListeners = this._eventListeners.filter(l => l !== docListener);
        }
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  _toggleMenu() {
    if (!this.disabled) {
      this.expanded = !this.expanded;
    }
  }

  _toggleOption(value) {
    if (this.singleSelect) {
      this.selected = this.selected.includes(value) ? [] : [value];
    } else {
      this.selected = this.selected.includes(value)
        ? this.selected.filter(v => v !== value)
        : [...this.selected, value];
    }
    if (this.preventNull && this.selected.length === 0) {
      this.selected = [this.options[0].value];
    }
    this._dispatchChange();
  }

  _dispatchChange() {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { selected: this.selected },
      bubbles: true,
      composed: true,
    }));
  }

  render() {
    const selectedCount = this.selected.length;
    const displayText = selectedCount > 0
      ? (this.singleSelect
          ? this.options.find(o => o.value === this.selected[0])?.label || this.placeholder
          : `Selected: ${selectedCount} option${selectedCount > 1 ? 's' : ''}`)
      : this.placeholder;

    const firstSelectedIcon = selectedCount > 0
      ? this.options.find(o => o.value === this.selected[0])?.icon || ''
      : '';

    return html`
      <div
        class="select-wrapper"
        role="combobox"
        aria-expanded="${this.expanded}"
        aria-multiselectable="${!this.singleSelect}"
        aria-disabled="${this.disabled}"
      >
        <button
          class="trigger ${this.disabled ? 'disabled' : ''} ${this.expanded ? 'expanded' : ''}"
          ?disabled="${this.disabled}"
          aria-label="Select options"
        >
          <slot name="trigger-icon">
            <studio-icon
              class="icon ${!firstSelectedIcon ? 'placeholder' : ''}"
              name="${firstSelectedIcon}"
              size="18px"
            ></studio-icon>
          </slot>
          <slot name="trigger-text">
            <span class="text">${displayText}</span>
          </slot>
          <slot name="arrow-icon">
            <studio-icon
              class="arrow"
              name="${this.expanded ? 'chevron-up' : 'chevron-down'}"
              size="18px"
            ></studio-icon>
          </slot>
        </button>
        ${this.expanded && !this.disabled
          ? html`
              <div class="menu" role="listbox" aria-label="Options">
                ${this.options.map((option, index) => {
                  const isSel = this.selected.includes(option.value);
                  return html`
                    <div
                      class="menu-item ${isSel ? 'selected' : ''}"
                      role="option"
                      aria-selected="${isSel}"
                      tabindex="0"
                      @click="${() => this._toggleOption(option.value)}"
                      @keydown="${(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          this._toggleOption(option.value);
                        }
                      }}"
                    >
                      <slot name="option-icon-${index}">
                        ${option.icon
                          ? html`<studio-icon class="icon" name="${option.icon}" size="16px"></studio-icon>`
                          : ''}
                      </slot>
                      <slot name="option-text-${index}">
                        <span class="option-text">${option.label}</span>
                      </slot>
                      <slot name="option-checkbox-${index}">
                        <span class="checkbox ${isSel ? 'checked' : ''}">
                          ${isSel
                            ? html`
                                <studio-icon
                                  name="check"
                                  size="16px"
                                  color="var(--color-white, #ffffff)"
                                ></studio-icon>
                              `
                            : ''}
                        </span>
                      </slot>
                    </div>
                  `;
                })}
              </div>
            `
          : ''}
        <slot></slot>
      </div>
    `;
  }
}

customElements.define('studio-select', StudioSelect);